import React, {useEffect, useMemo, useState} from 'react';
import {Form, Input, Progress, Select} from 'antd';
import TableAntD from "@common/TableAntD";
import PageHeader from "@common/PageHeader";
import Button from "@common/Button";
import TableFilterControls from "@common/TableFilterControls";
import TableFilterControlsForm from "@common/TableFilterControlsForm";
import TableFilterControlsSection from "@common/TableFilterControlsSection";
import {ArrowForwardRounded, ContentCopyRounded, DeleteRounded, PostAddRounded, SchoolRounded} from "@mui/icons-material";
import {useLocation} from "wouter";
import axios from "@common/axios";
import CreateScheduleDialog from "./components/CreateScheduleDialog";
import DuplicateScheduleDialog from "./components/DuplicateScheduleDialog";
import EditClassesDialog from "./components/EditClassesDialog";
import Tag from "@common/Tag";
import PopConfirm from "@common/PopConfirm";
import Tooltip from "@common/Tooltip";

const LessonPlannerSchedules = () => {
    const [, navigate] = useLocation();
    const [form] = Form.useForm();
    const [filters, setFilters] = useState({ name: '', classNames: null });
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [refetchTrigger, setRefetchTrigger] = useState(0);
    const [dialogVisible, setDialogVisible] = useState(false);
    const [editClassesTarget, setEditClassesTarget] = useState(null);
    const [duplicateTarget, setDuplicateTarget] = useState(null);

    useEffect(() => {
        setLoading(true);
        axios.get('/web/ui/lessonScheduler/plans')
            .then(r => setPlans(r.data))
            .finally(() => setLoading(false));
    }, [refetchTrigger]);

    const classOptions = useMemo(() => {
        const names = [...new Set(plans.flatMap(p => p.classNames || []).filter(Boolean))];
        return names.map(c => ({ label: c, value: c }));
    }, [plans]);

    const filteredData = useMemo(() => {
        return plans.filter(row => {
            if (filters.name && !row.name.toLowerCase().includes(filters.name.toLowerCase())) return false;
            if (filters.classNames && !(row.classNames || []).includes(filters.classNames)) return false;
            return true;
        });
    }, [filters, plans]);

    const handleValuesChange = (_, all) => setFilters({ name: all.name || '', classNames: all.classNames || null });

    const archivePlan = (scheduleId) => {
        axios.post(`/web/ui/lessonScheduler/plans/${scheduleId}/archive`)
            .then(() => setPlans(prev => prev.filter(p => p.scheduleId !== scheduleId)));
    };

    const columns = useMemo(() => [
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
            render: (text, row) => (
                <Button
                    icon={<ArrowForwardRounded />}
                    iconPosition={'right'}
                    type={'text'}
                    onClick={() => navigate(`/web/ui/view/lessonStudio/lessonSchedules/schedule/${row.scheduleId}`)}
                >
                    <strong>{text}</strong>
                </Button>
            ),
        },
        {
            title: 'Classes',
            dataIndex: 'classNames',
            key: 'classNames',
            render: (classNames) => {
                if (!classNames || classNames.length === 0) return null;
                return (
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                        {classNames.map(name => <Tag key={name}>{name}</Tag>)}
                    </div>
                );
            },
        },
        {
            title: 'Progress',
            key: 'progress',
            width: 200,
            render: (_, row) => {
                const total = row.lessonCount || 0;
                const done = row.completeLessonCount || 0;
                const pct = total > 0 ? Math.round((done / total) * 100) : 0;
                const strokeColor = pct === 100 ? '#52c41a' : '#1677ff';
                return (
                    <div
                        role="meter"
                        aria-valuenow={done}
                        aria-valuemin={0}
                        aria-valuemax={total}
                        aria-label={`Lesson progress: ${done} of ${total} lessons complete`}
                    >
                        <Progress
                            percent={pct}
                            showInfo={false}
                            strokeColor={strokeColor}
                            railColor="rgba(0,0,0,0.12)"
                            size="small"
                        />
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '12px', color: 'var(--on-surface-mid-colour)' }}>
                            <span>{done} / {total} lessons done</span>
                            <span>{pct}%</span>
                        </div>
                    </div>
                );
            },
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 130,
            render: (_, row) => (
                <div style={{ display: 'flex', gap: '4px' }}>
                    <Tooltip title="Edit classes">
                        <Button type={'text'} onClick={() => setEditClassesTarget(row)}>
                            <SchoolRounded style={{ fontSize: '18px' }} />
                        </Button>
                    </Tooltip>
                    <Tooltip title="Duplicate">
                        <Button type={'text'} onClick={() => setDuplicateTarget(row)}>
                            <ContentCopyRounded style={{ fontSize: '18px' }} />
                        </Button>
                    </Tooltip>
                    <PopConfirm
                        title={`Delete "${row.name}"?`}
                        description="This will permanently delete the schedule and all its units and lessons."
                        onConfirm={() => archivePlan(row.scheduleId)}
                        okText="Delete"
                    >
                        <>
                            <Tooltip title="Delete">
                                <Button type={'text'}>
                                    <DeleteRounded style={{ fontSize: '18px' }} />
                                </Button>
                            </Tooltip>
                        </>
                    </PopConfirm>
                </div>
            ),
        },
    ], [navigate, archivePlan]);

    return (
        <div>
            <PageHeader
                title={'Lesson Schedules'}
                subtitle={'Schedule lessons across your classes for the term.'}
                buttons={
                    <Button icon={<PostAddRounded />} onClick={() => setDialogVisible(true)}>New Schedule</Button>
                }
            />
            <TableFilterControls
                filterContent={
                    <TableFilterControlsForm form={form} onValuesChange={handleValuesChange}>
                        <TableFilterControlsSection showDivider={false}>
                            <Form.Item name="name" label="Name">
                                <Input placeholder="Search by name" allowClear />
                            </Form.Item>
                            <Form.Item name="classNames" label="Class">
                                <Select options={classOptions} placeholder="All classes" allowClear={true}/>
                            </Form.Item>
                        </TableFilterControlsSection>
                    </TableFilterControlsForm>
                }
            />
            <TableAntD
                dynamicWidth={true}
                columnData={columns}
                data={filteredData}
                scroll={{x: true}}
                rowKey="scheduleId"
                loading={loading}
                locale={{emptyText: 'No lesson schedules'}}
            />
            <EditClassesDialog
                scheduleId={editClassesTarget?.scheduleId}
                visible={editClassesTarget !== null}
                handleClose={() => setEditClassesTarget(null)}
                plan={editClassesTarget}
                onSuccess={() => setRefetchTrigger(t => t + 1)}
            />
            <DuplicateScheduleDialog
                visible={duplicateTarget !== null}
                handleClose={() => setDuplicateTarget(null)}
                plan={duplicateTarget}
                onSuccess={() => {
                    setDuplicateTarget(null);
                    setRefetchTrigger(t => t + 1);
                }}
            />
            <CreateScheduleDialog
                visible={dialogVisible}
                handleClose={() => setDialogVisible(false)}
                onSuccess={() => {
                    setDialogVisible(false);
                    setRefetchTrigger(t => t + 1);
                }}
            />
        </div>
    );
};

export default LessonPlannerSchedules;
